import { MONO } from './base.jsx';
import { STATUTS } from '../data/postes.js';
import { BallonPlein, Passe, Entree, Carton } from './icones.jsx';

const TON = { bon: 'var(--acc2)', attention: 'var(--warn)', mauvais: 'var(--bad)' };

/**
 * Un jeton sur le terrain.
 *
 * Le bouton fait EXACTEMENT la taille du rond, et l'étiquette du nom est posée
 * dessous en absolu. Une version antérieure empilait rond et nom dans une colonne
 * centrée sur le point de la formation : c'est la colonne qui se trouvait centrée,
 * donc chaque joueur était remonté de la moitié de la hauteur de son étiquette, et
 * l'écart grandissait avec la longueur du nom. Le rond doit être sur le point.
 *
 * Il n'y a pas de glisser-déposer : déplacer un joueur se fait en deux touchers (le
 * joueur, puis sa destination), ce qui marche debout, d'une main, et ne peut pas rater
 * sa cible — là où un glisser précis sur 360 px rate, et oblige à défaire.
 */
export function Jeton({ joueur, code, gauche, haut, taille, joueurs, selection, cible, evenements = [], entreA, onTap }) {
  const vide = !joueur;
  const statut = joueur ? STATUTS[joueur.statut] : null;
  const alerte = statut && statut.ton !== 'bon';
  const buts = evenements.filter((e) => e.type === 'but').length;
  const passes = evenements.filter((e) => e.type === 'passe').length;
  const jaunes = joueur?.jaunes ?? 0;
  const rouge = (joueur?.rouges ?? 0) > 0 || jaunes >= 2;   // deux jaunes = expulsion

  const pastille = Math.max(9, Math.round(taille * 0.28));

  return (
    <button
      onClick={onTap}
      aria-label={vide ? `Poste ${code} libre` : `${joueur.nom}, ${code}`}
      style={{
        position: 'absolute', left: gauche, top: haut, width: taille, height: taille,
        transform: 'translate(-50%,-50%)', padding: 0,
        // La bascule avec/sans ballon fait GLISSER les onze jetons : c'est le mouvement
        // qui fait comprendre que la même équipe recule, là où deux images fixes
        // n'apprendraient rien.
        transition: 'left .25s cubic-bezier(.4,0,.2,1), top .25s cubic-bezier(.4,0,.2,1)',
      }}
    >
      <span style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: MONO, fontWeight: 700,
        fontSize: Math.round(taille * (vide ? 0.44 : 0.38)),
        background: vide ? 'transparent' : 'var(--acc)',
        color: vide ? 'var(--ink2)' : 'var(--tokink)',
        border: vide ? '1.5px dashed var(--pline)' : 'none',
        boxShadow: cible ? '0 0 0 3px var(--acc2)' : selection ? '0 0 0 3px var(--ink)' : 'none',
      }}>
        {vide ? '+' : (joueur.numero ?? '·')}
      </span>

      {alerte && (
        <span style={{
          position: 'absolute', right: -1, top: -1, width: pastille, height: pastille,
          borderRadius: '50%', background: TON[statut.ton], border: '2px solid var(--pitch)',
        }} />
      )}

      {/* LE CARTON DU MATCH EN COURS, dès le premier.
          Une version antérieure n'affichait qu'un liseré à partir de TROIS jaunes :
          elle confondait deux choses distinctes. Le cumul de la saison (cinq jaunes =
          un match de suspension) se lit sur la fiche ; ce qui doit sauter aux yeux sur
          le terrain, c'est le carton pris il y a deux minutes. Deux jaunes dans le
          même match valent un rouge, et le jeton le dit. */}
      {(jaunes > 0 || rouge) && (
        <span style={{
          position: 'absolute', left: -Math.round(taille * 0.12), top: -Math.round(taille * 0.06),
          display: 'flex', alignItems: 'center', pointerEvents: 'none',
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.6))',
        }}>
          <Carton t={Math.max(13, Math.round(taille * 0.42))} couleur={rouge ? 'var(--bad)' : 'var(--warn)'} />
          {jaunes > 1 && !rouge && (
            <span style={{ fontSize: Math.max(8, Math.round(taille * 0.24)), fontWeight: 800, color: 'var(--warn)' }}>{jaunes}</span>
          )}
        </span>
      )}

      {/* Le cumul de la SAISON, lui, reste un liseré discret : à trois jaunes on
          commence à y penser, à quatre le prochain coûte un match. */}
      {(joueur?.jaunesSaison ?? 0) >= 3 && (
        <span style={{
          position: 'absolute', inset: -3, borderRadius: '50%', pointerEvents: 'none',
          border: `2px solid ${(joueur.jaunesSaison >= 4) ? 'var(--bad)' : 'var(--warn)'}`,
        }} />
      )}

      {joueur?.capitaine && (
        <span style={{
          position: 'absolute', left: -3, bottom: -1,
          width: pastille + 3, height: pastille + 3, borderRadius: '50%',
          background: 'var(--ink)', color: 'var(--bg)',
          fontSize: Math.round(pastille * 0.72), fontWeight: 800,
          lineHeight: `${pastille + 3}px`, textAlign: 'center',
        }}>C</span>
      )}

      {(buts > 0 || passes > 0) && (
        <span style={{
          position: 'absolute', right: -Math.round(taille * 0.16), bottom: -2,
          display: 'flex', alignItems: 'center', gap: 1,
          padding: '1px 4px', borderRadius: 7,
          background: 'var(--ink)', color: 'var(--bg)',
          fontSize: Math.max(8, Math.round(taille * 0.24)), fontWeight: 800,
        }}>
          {buts > 0 && <><BallonPlein t={Math.max(9, Math.round(taille * 0.3))} />{buts > 1 ? buts : null}</>}
          {passes > 0 && <span style={{ opacity: .8, display: 'flex', alignItems: 'center' }}><Passe t={Math.max(9, Math.round(taille * 0.3))} />{passes > 1 ? passes : null}</span>}
        </span>
      )}

      {/* L'étiquette sort du bouton : elle n'entre donc pas dans son centrage, et elle
          ne capte pas le toucher — c'est le rond qu'on vise. */}
      <span style={{
        position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
        marginTop: 3, pointerEvents: 'none', whiteSpace: 'nowrap',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
      }}>
        {vide ? (
          <span style={{ fontSize: Math.max(8, Math.round(taille * 0.24)), fontFamily: MONO, fontWeight: 600, color: 'var(--ink2)', letterSpacing: '.4px' }}>{code}</span>
        ) : (
          <>
            <span style={{
              padding: '1px 5px', borderRadius: 4, background: 'rgba(0,0,0,.5)', color: '#fff',
              fontSize: Math.max(8.5, Math.round(taille * 0.26)), fontWeight: 700,
              maxWidth: taille * 2.4, overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{court(joueur.nom, joueurs)}</span>
            {/* « Il est entré quand, celui-là ? » est la question de la 88e minute. */}
            {entreA != null && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: Math.max(7.5, Math.round(taille * 0.22)), fontFamily: MONO, fontWeight: 700, color: 'var(--acc2)' }}>
                <Entree t={Math.max(8, Math.round(taille * 0.26))} />{entreA}&apos;
              </span>
            )}
          </>
        )}
      </span>
    </button>
  );
}

/**
 * Le nom sous un rond de 38 px ne peut pas faire « Labeau-Lascary ».
 *
 * On réduit au patronyme seul, et on ne remet l'initiale du prénom QUE si un autre
 * joueur de l'effectif porte le même patronyme — sinon on abrège pour rien, et deux
 * Fofana sur le terrain deviendraient indistinguables.
 */
function court(nom, joueurs) {
  const parts = String(nom).trim().split(/\s+/);
  if (parts.length < 2) return nom;
  const patronyme = parts.slice(1).join(' ');
  const homonyme = joueurs?.some((j) => j.nom !== nom && j.nom.trim().split(/\s+/).slice(1).join(' ') === patronyme);
  return homonyme ? `${parts[0][0]}. ${patronyme}` : patronyme;
}
